import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { map, switchMap } from 'rxjs/operators';
import { AuthService, AuthUser } from '../services/auth.service';
import { AdminApiService } from '../services/admin-api.service';
import { UploadServiceService } from '../service/upload-service.service';

const MAX_RESUME_SIZE = 10 * 1024 * 1024;
const ALLOWED_EXTENSIONS = new Set(['pdf', 'doc', 'docx']);

@Component({
  selector: 'app-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.css'],
  standalone: false
})
export class ProfileComponent implements OnInit {
  currentUser: AuthUser | null = null;
  profile: any = null;
  selectedFile: File | null = null;
  loading = true;
  uploading = false;
  requiredForAssessment = false;
  assessmentId: string | null = null;

  constructor(
    private authService: AuthService,
    private adminApi: AdminApiService,
    private uploadService: UploadServiceService,
    private route: ActivatedRoute,
    private router: Router,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.currentUser = this.authService.getCurrentUser();
    this.requiredForAssessment = this.route.snapshot.queryParamMap.get('required') === 'true';
    this.assessmentId = this.route.snapshot.queryParamMap.get('assessmentId');

    if (!this.currentUser?.userId) {
      this.router.navigate(['/login']);
      return;
    }
    this.loadProfile();
  }

  loadProfile(): void {
    if (!this.currentUser?.userId) return;
    this.loading = true;
    this.adminApi.getUserById(this.currentUser.userId).subscribe({
      next: (response) => {
        this.profile = response?.user || null;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.snackBar.open('Unable to load your profile.', 'Dismiss', { duration: 5000 });
      }
    });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] || null;
    if (!file) return;

    const extension = this.fileExtension(file.name);
    if (!ALLOWED_EXTENSIONS.has(extension)) {
      this.selectedFile = null;
      input.value = '';
      this.snackBar.open('Please select a PDF, DOC, or DOCX resume.', 'Dismiss', { duration: 5000 });
      return;
    }
    if (file.size > MAX_RESUME_SIZE) {
      this.selectedFile = null;
      input.value = '';
      this.snackBar.open('Resume files must be 10 MB or smaller.', 'Dismiss', { duration: 5000 });
      return;
    }
    this.selectedFile = file;
  }

  uploadResume(): void {
    const userId = this.currentUser?.userId;
    const file = this.selectedFile;
    if (!userId || !file || this.uploading) return;

    const extension = this.fileExtension(file.name);
    const fileId = this.generateFileId();
    const objectBase = `resumes/${userId}/${fileId}`;
    const resumeKey = `${objectBase}.${extension}`;
    this.uploading = true;

    this.uploadService.getUploadURL(objectBase, extension).pipe(
      map((response) => this.extractUploadUrl(response)),
      switchMap((uploadUrl) => this.uploadService.postFileOnS3(uploadUrl, file).pipe(
        map(() => uploadUrl.split('?')[0])
      )),
      switchMap((resumeUrl) => this.adminApi.setUserResume(userId, resumeUrl, resumeKey))
    ).subscribe({
      next: (response) => {
        this.profile = response?.user || { ...this.profile, resumeUrl: response?.resumeUrl };
        this.selectedFile = null;
        this.uploading = false;
        this.snackBar.open('Resume uploaded successfully.', 'Dismiss', { duration: 3500 });
        if (this.requiredForAssessment && this.assessmentId) {
          this.router.navigate(['/start-assessment'], { queryParams: { assessmentId: this.assessmentId } });
        }
      },
      error: (error) => {
        console.error('Resume upload failed', error);
        this.uploading = false;
        this.snackBar.open('Unable to upload your resume. Please try again.', 'Dismiss', { duration: 5000 });
      }
    });
  }

  viewResume(): void {
    if (this.profile?.resumeUrl) {
      window.open(this.profile.resumeUrl, '_blank', 'noopener,noreferrer');
    }
  }

  skipForNow(): void {
    this.router.navigate(['/assesments']);
  }

  private extractUploadUrl(response: any): string {
    let payload = response?.body ?? response;
    if (typeof payload === 'string') payload = JSON.parse(payload);
    if (!payload?.uploadURL) throw new Error('Upload URL was not returned');
    return payload.uploadURL;
  }

  private fileExtension(filename: string): string {
    return filename.split('.').pop()?.toLowerCase() || '';
  }

  private generateFileId(): string {
    return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}
